import { normalizeRing, uppercaseText } from './domain'
import type { BirdInput, ChickInput, SheetDraft } from './types'

export type SpokenSheetProposal = {
  padre: Partial<BirdInput>
  madre: Partial<BirdInput>
  pichones: Partial<BirdInput>[]
}

export function normalizeSpokenSex(value: string) {
  const compact = uppercaseText(value).replace(/[.\s]/g, '')
  if (compact === 'M' || compact === 'MACHO' || compact === 'MASCULINO') return 'MACHO'
  if (compact === 'H' || compact === 'HEMBRA' || compact === 'FEMENINO') return 'HEMBRA'
  return 'DUDOSO'
}

function searchable(value: string) {
  return uppercaseText(value)
    .replace(/Á/g, 'A').replace(/É/g, 'E').replace(/Í/g, 'I').replace(/Ó/g, 'O').replace(/Ú/g, 'U')
}

function cleanSpokenValue(value: string) {
  return uppercaseText(value)
    .replace(/^[\s,:;.\-]+|[\s,:;.\-]+$/g, '')
    .replace(/^(?:ES|SON|DE|DEL|LA|EL|Y|CON|TIENE)\s+/i, '')
    .replace(/\s+(?:Y|CON)$/i, '')
    .trim()
}

function parseSection(text: string, parent: boolean): Partial<BirdInput> {
  const plain = searchable(text)
  const markers = [...plain.matchAll(/\b(NOMBRE|PLUMAJE|ANILLA|SEXO|OBSERVACION|OBSERVACIONES)\b/g)]
  const result: Partial<BirdInput> = {}

  const leading = cleanSpokenValue(text.slice(0, markers[0]?.index ?? text.length))
  if (parent) {
    const name = leading.replace(/^(?:SE LLAMA|NOMBRE)\s+/i, '')
    if (name) result.nombre = name
  } else if (leading) {
    const ring = normalizeSpokenRing(leading)
    if (/^[A-ZÑ]{1,4}-\d+$/.test(ring)) result.anilla = ring
  }

  markers.forEach((marker, index) => {
    const start = (marker.index || 0) + marker[0].length
    const end = markers[index + 1]?.index ?? text.length
    const value = cleanSpokenValue(text.slice(start, end))
    if (!value) return
    switch (marker[1]) {
      case 'NOMBRE': result.nombre = value; break
      case 'PLUMAJE': result.plumaje = value; break
      case 'ANILLA': result.anilla = normalizeSpokenRing(value); break
      case 'SEXO': result.sexo = normalizeSpokenSex(value); break
      default: result.observaciones = value
    }
  })
  return result
}

export function mergeSpokenProposal(draft: SheetDraft, proposal: SpokenSheetProposal): SheetDraft {
  const mergeBird = (base: BirdInput, patch: Partial<BirdInput>): BirdInput => ({ ...base, ...patch })
  const pichones = proposal.pichones.length
    ? proposal.pichones.map((patch, index) => mergeBird(
      draft.pichones[index] || { id: crypto.randomUUID(), nombre: '', anilla: '', plumaje: '', sexo: '', observaciones: '' },
      patch,
    ) as ChickInput)
    : draft.pichones
  return {
    ...draft,
    padre: mergeBird(draft.padre, proposal.padre),
    madre: mergeBird(draft.madre, proposal.madre),
    pichones,
  }
}
export function parseSpokenSheet(transcript: string): SpokenSheetProposal {
  const text = uppercaseText(transcript)
  const plain = searchable(text)
  const rolePattern = /\b(PADRE|MADRE|(?:(?:PRIMER|PRIMERO|SEGUNDO|TERCER|TERCERO|CUARTO|QUINTO|SEXTO|SEPTIMO|OCTAVO|NOVENO|DECIMO|\d+)\s+)?PICHON)\b/g
  const roles = [...plain.matchAll(rolePattern)]
  const proposal: SpokenSheetProposal = { padre: {}, madre: {}, pichones: [] }

  roles.forEach((role, index) => {
    const start = (role.index || 0) + role[0].length
    const end = roles[index + 1]?.index ?? text.length
    const section = text.slice(start, end)
    if (role[1] === 'PADRE') proposal.padre = parseSection(section, true)
    else if (role[1] === 'MADRE') proposal.madre = parseSection(section, true)
    else proposal.pichones.push(parseSection(section, false))
  })
  return proposal
}
export function classifyMoreChicks(value: string): 'yes' | 'no' | 'unknown' {
  const answer = uppercaseText(value).replace(/[ÁÀ]/g, 'A').replace(/[ÉÈ]/g, 'E').replace(/[ÍÌ]/g, 'I').replace(/[ÓÒ]/g, 'O').replace(/[ÚÙ]/g, 'U')
  if (/\b(NO|NINGUNO|NINGUNA|YA ESTA|TERMINAR|FIN)\b/.test(answer)) return 'no'
  if (/\b(SI|OTRO|OTRA|MAS|CONTINUAR|SIGUIENTE)\b/.test(answer)) return 'yes'
  return 'unknown'
}
const spokenDigits: Record<string, string> = {
  CERO: '0', UNO: '1', UN: '1', UNA: '1', DOS: '2', TRES: '3', CUATRO: '4', CINCO: '5', SEIS: '6', SIETE: '7', OCHO: '8', NUEVE: '9'
}
const smallNumbers: Record<string, number> = {
  DIEZ: 10, ONCE: 11, DOCE: 12, TRECE: 13, CATORCE: 14, QUINCE: 15, DIECISEIS: 16, DIECISIETE: 17, DIECIOCHO: 18, DIECINUEVE: 19,
  VEINTE: 20, VEINTIUNO: 21, VEINTIDOS: 22, VEINTITRES: 23, VEINTICUATRO: 24, VEINTICINCO: 25, VEINTISEIS: 26, VEINTISIETE: 27, VEINTIOCHO: 28, VEINTINUEVE: 29,
  TREINTA: 30, CUARENTA: 40, CINCUENTA: 50, SESENTA: 60, SETENTA: 70, OCHENTA: 80, NOVENTA: 90
}
const hundreds: Record<string, number> = {
  CIEN: 100, CIENTO: 100, DOSCIENTOS: 200, TRESCIENTOS: 300, CUATROCIENTOS: 400, QUINIENTOS: 500,
  SEISCIENTOS: 600, SETECIENTOS: 700, OCHOCIENTOS: 800, NOVECIENTOS: 900
}
const spokenLetters: Record<string, string> = {
  A: 'A', BE: 'B', B: 'B', CE: 'C', C: 'C', DE: 'D', D: 'D', E: 'E', EFE: 'F', F: 'F', GE: 'G', G: 'G',
  HACHE: 'H', H: 'H', I: 'I', JOTA: 'J', J: 'J', KA: 'K', K: 'K', ELE: 'L', L: 'L', EME: 'M', M: 'M',
  ENE: 'N', N: 'N', ENYE: 'Ñ', Ñ: 'Ñ', O: 'O', PE: 'P', P: 'P', CU: 'Q', Q: 'Q', ERRE: 'R', R: 'R',
  ESE: 'S', S: 'S', TE: 'T', T: 'T', U: 'U', UVE: 'V', V: 'V', DOBLEUVE: 'W', W: 'W', EQUIS: 'X', X: 'X', YE: 'Y', Y: 'Y', ZETA: 'Z', Z: 'Z'
}

function parseCardinal(tokens: string[]) {
  let total = 0
  let current = 0
  tokens.forEach(token => {
    if (token === 'Y') return
    if (/^\d+$/.test(token)) { current += Number(token); return }
    if (hundreds[token] !== undefined) { current += hundreds[token]; return }
    if (smallNumbers[token] !== undefined) { current += smallNumbers[token]; return }
    if (spokenDigits[token] !== undefined) { current += Number(spokenDigits[token]); return }
    if (token === 'MIL') { total += (current || 1) * 1000; current = 0 }
    if (token === 'MILLON' || token === 'MILLONES') { total = (total + (current || 1)) * 1000000; current = 0 }
  })
  return String(total + current)
}

export function normalizeSpokenRing(value: string) {
  const tokens = searchable(value).replace(/[^A-ZÑ0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean)
    .filter(token => !['ANILLA', 'NUMERO', 'NÚMERO', 'LA', 'ES'].includes(token))
  const isNumber = (token: string) => /^\d+$/.test(token) || spokenDigits[token] !== undefined || smallNumbers[token] !== undefined || hundreds[token] !== undefined || ['MIL', 'MILLON', 'MILLONES'].includes(token)
  const numberStart = tokens.findIndex(isNumber)
  if (numberStart < 0) return normalizeRing(searchable(value))
  const prefix = tokens.slice(0, numberStart).map(token => spokenLetters[token] || (/^[A-Z]{1,4}$/.test(token) ? token : '')).join('')
  const numberTokens = tokens.slice(numberStart).filter(token => token !== 'Y')
  const encodeNumberTokens = (parts: string[]): string => {
    const digitSequence = parts.every(token => /^\d+$/.test(token) || spokenDigits[token] !== undefined)
    if (digitSequence) return parts.map(token => /^\d+$/.test(token) ? token : spokenDigits[token]).join('')

    // En dictados mixtos se suelen agrupar números: «cincuenta y cinco, cero cero cero cinco»
    // o «quinientos cincuenta, 103». Conservamos cada grupo y después los concatenamos.
    if (!parts.some(token => ['MIL', 'MILLON', 'MILLONES'].includes(token))) {
      let seenHundreds = false
      for (let index = 0; index < parts.length; index += 1) {
        const token = parts[index]
        const splitAtLiteral = index > 0 && /^\d+$/.test(token)
        const splitAtZero = index > 0 && token === 'CERO'
        const splitAtNewHundred = index > 0 && hundreds[token] !== undefined && seenHundreds
        if (splitAtLiteral || splitAtZero || splitAtNewHundred) {
          return `${encodeNumberTokens(parts.slice(0, index))}${encodeNumberTokens(parts.slice(index))}`
        }
        if (hundreds[token] !== undefined) seenHundreds = true
      }
    }
    return parseCardinal(parts)
  }
  const digits = encodeNumberTokens(numberTokens)
  return normalizeRing(`${prefix}${digits}`)
}

export function normalizeSpokenSearch(value: string) {
  const ring = normalizeSpokenRing(value)
  return /^[A-ZÑ]{1,4}-\d+$/.test(ring) ? ring : uppercaseText(value)
}